import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { NotificationService } from 'app/_services/notification.service';
import { SiteOwnerUserService } from 'app/_services/site-owner-user.service';
import { SiteService } from 'app/_services/site.service';
import { TrainingService } from 'app/_services/training.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-site-details',
  templateUrl: './site-details.component.html'
})
export class SiteDetailsComponent implements OnInit {
  private routeRole: any;

  public selectedSite: any;
  private selectedSiteId: any;

  public siteDetailsForm: FormGroup;

  public siteOwners: any[] = [];
  public selectedOwner: any;
  public availableInductions: any[] = [];
  public selectedInduction = null;

  constructor(
    private siteService: SiteService,
    private activatedRoute: ActivatedRoute,
    private trainingService: TrainingService,
    private notificationService: NotificationService,
    private siteOwnerUserService: SiteOwnerUserService,
    private router: Router,
    globals: Globals,
    fb: FormBuilder,
  ) {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedSiteId = params.id;
    });

    this.routeRole = globals.user.routeRole;

    this.siteDetailsForm = fb.group({
      name: new FormControl(),
      mainContactNumber: new FormControl(),
      emergencyContactNumber: new FormControl(),
      altEmergencyContactNumber: new FormControl(),
      // owner select works separately to form
      description: new FormControl(),
      waypointInterval: new FormControl(),
      // induction select works separately to form
      addAssetCompetencies: new FormControl(false),
    });
  }

  ngOnInit() {
    this.getSelectedSite(this.selectedSiteId);
    this.getAllSiteOwners();
  }

  private getSelectedSite(id) {
    this.siteService.getSiteById(id).subscribe((data: any) => {
      this.selectedSite = data;

      this.getAllSiteInductionsByOwner(data.siteOwner.id);

      if (!!this.selectedSite.siteInductionCourse) {
        this.selectedInduction = {
          label: this.selectedSite.siteInductionCourse.name,
          value: this.selectedSite.siteInductionCourse.id
        };
      }

      this.selectedOwner = { label: data.siteOwner.firstName + ' ' + data.siteOwner.lastName, value: data.siteOwner };
      this.assignFormValues();
    });
  }


  private getAllSiteInductionsByOwner(id) {
    this.availableInductions = [];
    this.trainingService.getTrainingCoursesBySiteOwnerId(id).subscribe((data: any) => {
      const inductions = data.filter(course => course.courseType === 'INDUCTION');

      inductions.forEach(element => {
        const obj = {
          label: element.name,
          value: element.id
        };
        this.availableInductions.push(obj);
      });
    });
  }

  private assignFormValues() {
    this.siteDetailsForm.patchValue({
      name: this.selectedSite.name,
      mainContactNumber: this.selectedSite.mainContactNumber,
      emergencyContactNumber: this.selectedSite.emergencyContactNumber,
      altEmergencyContactNumber: this.selectedSite.altEmergencyContactNumber,
      description: this.selectedSite.description,
      waypointInterval: this.selectedSite.healthCheckInterval,
    })

    if (this.selectedSite.addAssetCompetencies !== null) {
      this.siteDetailsForm.patchValue({
        addAssetCompetencies: this.selectedSite.addAssetCompetencies
      })
    } else {
      this.siteDetailsForm.patchValue({
        addAssetCompetencies: false
      })
    }
  }

  private getAllSiteOwners() {
    this.siteOwnerUserService.getAllEnabledSiteOwnersForDropdowns().subscribe((data: any) => {
      this.siteOwners.push(this.selectedOwner);
      data.forEach(element => {
        const obj = {
          label: element.firstName + ' ' + element.lastName,
          value: element
        };
        this.siteOwners.push(obj);
      });
    });
  }

  public onSiteDetailsFormSubmit() {
    const values = this.siteDetailsForm.value;
    const newDetails = {
      name: values.name,
      mainContactNumber: values.mainContactNumber,
      emergencyContactNumber: values.emergencyContactNumber,
      altEmergencyContactNumber: values.altEmergencyContactNumber,
      description: values.description,
      healthCheckInterval: values.waypointInterval,
      addAssetCompetencies: values.addAssetCompetencies,
    };

    const body = this.siteService.createRequestBodyForEditingSite(this.selectedSite, newDetails);

    this.siteService.editSiteDetails(body).subscribe((data: any) => {
      this.getSelectedSite(this.selectedSiteId);
      this.notificationService.notify('success', 'Success', 'Site Details Updated');
    });
  }

  public onSelectedSiteInductionChange(event) {
    this.trainingService.addInductionToSite(this.selectedSiteId, event.value).subscribe((data: any) => {
      this.getSelectedSite(this.selectedSiteId);
      this.notificationService.notify('success', 'Success', 'Site Induction Updated');
    });
  }

  public onSelectedSiteOwnerChange(event) {
    const body = {
      siteId: this.selectedSiteId,
      siteOwnerSkylarkId: event.value.id
    };
    this.siteService.updateSiteOwner(body).subscribe((data: any) => {
      this.getSelectedSite(this.selectedSiteId);
      this.getAllSiteOwners();
      this.removeInduction();
      this.notificationService.notify('success', 'Success', 'Site Owner Updated');
    });
  }

  public removeInduction() {
    this.trainingService.removeInductionFromSite(this.selectedSiteId, this.selectedInduction.value).subscribe((data: any) => {
      this.selectedInduction = null;
      this.getSelectedSite(this.selectedSiteId);
      this.notificationService.notify('success', 'Success', 'Site Induction Removed');
    });
  }

  public deleteSite() {
    Swal.fire({
      title: 'Delete Site?',
      text: 'You will not be able to revert this!',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.value) {
        this.siteService.deleteSite(this.selectedSiteId).subscribe((data: any) => {
          this.router.navigate(['/' + this.routeRole + '/sites'])
          this.notificationService.notify('success', 'Success', 'Site has been removed');
        })
      }
    });
  }
}
